import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../mail/SendMailScreen.css';
import '../styles/MainContainer.css';
import './RoundMail.css';
import { useParams } from 'react-router-dom';
import api from '../wrapper/API';
import { useSuccessBar } from '../context/SuccessBarContext';
import { usePopup } from '../context/PopupContext';
import Popup from '../popup/Popup';
import Textfield from '../input/Textfield';
import CustomDropdown from '../input/CustomDropdown';
import ActionButton from '../input/ActionButton';
import Icon from '../home/Icon';
import Tooltip from '../tooltip/Tooltip';
import OfflineBanner from '../shell/OfflineBanner';
import RecipientPreview from '../mail/RecipientPreview';
import { ParticipantGroupChip } from '../participant-group/ParticipantGroupChips';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import { resolveGroupAndRole } from '../../util/participantGroups';
import { eventLabelOf } from '../../util/eventRules';
import { pickTextblockContent } from '../../util/textblockVariants';
import TextblockVariantHint from '../mail/TextblockVariantHint';

import { useEditor, BubbleMenu, EditorContent } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import BulletList from '@tiptap/extension-bullet-list';
import ListItem from '@tiptap/extension-list-item';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import History from '@tiptap/extension-history';
import { Color } from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import HardBreak from '@tiptap/extension-hard-break';

const isConfirmed = (p) => p.participantType === 'Confirmed' || p.participantType === 1;

// Rundmail an die bestätigten Teilnehmer eines Spieltags. Empfänger = Snapshot-Gruppe am Spieltag ist
// ausgewählt + E-Mail-Adresse vorhanden (gleiche Regel wie POST /Textblock/SendRoundMail). Vorschau und
// Anzahl entstehen aus der bereits geladenen Teilnehmerliste — kein zusätzlicher Request.
function RoundMail() {
  const { eventId } = useParams();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const { openPopup } = usePopup();
  const { groups, byId: groupsById, guest, loaded: groupsLoaded, failed: groupsFailed } = useParticipantGroups();

  const [event, setEvent] = useState(null);
  // null = noch nicht vorbelegt; Default: nur Gäste (bisheriges Verhalten der Rundmail).
  const [selectedGroupIds, setSelectedGroupIds] = useState(null);
  const [textblocks, setTextblocks] = useState([]);
  const [selectedTextblockId, setSelectedTextblockId] = useState(null);
  // Location variant whose text was put into the editor (null = default text).
  const [usedVariant, setUsedVariant] = useState(null);
  const [subject, setSubject] = useState('');
  const [error, setError] = useState('');

  const editor = useEditor({
    extensions: [
      Document, Paragraph, Text, BulletList, ListItem, Bold, Italic,
      Link.configure({ openOnClick: false, autolink: true, defaultProtocol: 'https' }),
      Underline, History, Color, TextStyle, HardBreak,
    ],
    content: '',
  });

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  useEffect(() => {
    api.get(`/Event/GetById?eventId=${eventId}`)
      .then(response => setEvent(response.data))
      .catch(() => {});

    api.get('/Textblock/GetAllNames?language=DE')
      .then(response => setTextblocks(response.data))
      .catch(() => {});
  }, [eventId]);

  useEffect(() => {
    if (selectedGroupIds == null && groupsLoaded) setSelectedGroupIds(guest ? [guest.id] : []);
  }, [selectedGroupIds, groupsLoaded, guest]);

  // Bestätigte Teilnehmer mit E-Mail, je mit ihrer Gruppe am Spieltag.
  const mailable = useMemo(() => (event?.participants ?? [])
    .filter(p => isConfirmed(p) && p.email)
    .map(p => ({ row: p, groupId: resolveGroupAndRole(p, groupsById).group?.id ?? null })),
  [event, groupsById]);

  const countByGroup = useMemo(() => {
    const counts = new Map();
    mailable.forEach(({ groupId }) => counts.set(groupId, (counts.get(groupId) ?? 0) + 1));
    return counts;
  }, [mailable]);

  // Aktive Gruppen; archivierte nur, wenn Teilnehmer dieses Spieltags noch zu ihnen gehören.
  const chipGroups = useMemo(
    () => groups.filter(g => !g.isArchived || (countByGroup.get(g.id) ?? 0) > 0),
    [groups, countByGroup]
  );

  const selectedSet = useMemo(() => new Set(selectedGroupIds ?? []), [selectedGroupIds]);
  const recipients = useMemo(
    () => mailable.filter(m => selectedSet.has(m.groupId)).map(m => m.row),
    [mailable, selectedSet]
  );

  const toggleGroup = (id) => setSelectedGroupIds(prev => {
    const current = prev ?? [];
    return current.includes(id) ? current.filter(x => x !== id) : [...current, id];
  });

  if (!editor) return null;

  const handleDropdownChange = (e) => {
    const id = e.target.value;
    const textblock = id ? textblocks.find(tb => String(tb.id) === String(id)) : null;
    if (!textblock) {
      setSelectedTextblockId(null);
      setUsedVariant(null);
      setSubject('');
      editor.commands.setContent('');
      return;
    }
    setSelectedTextblockId(textblock.id);

    // Raw text (placeholders stay; the server fills them per recipient) from the already loaded list —
    // the variant for the event's location if the block has one, else the default.
    const content = pickTextblockContent(textblock, event?.locationId);
    editor.commands.setContent(content.body);
    setSubject(content.subject);
    setUsedVariant(content.variant);
  };

  const sendMail = () => {
    if (!subject.trim()) {
      setError('Bitte gib einen Betreff ein.');
      openPopup('roundmailerror', 1);
      return;
    }
    // Leere Auswahl nie senden — das Backend würde sie als "nur Gäste" auslegen.
    if (selectedSet.size === 0) {
      setError('Bitte wähle mindestens eine Gruppe als Empfänger aus.');
      openPopup('roundmailerror', 1);
      return;
    }
    if (recipients.length === 0) {
      setError('In den gewählten Gruppen gibt es keine bestätigten Teilnehmer mit E-Mail-Adresse.');
      openPopup('roundmailerror', 1);
      return;
    }

    startSuccessBarLoading('Rundmail wird in Versand-Queue gestellt');
    api.post('/Textblock/SendRoundMail', {
      eventId: parseInt(eventId),
      subject,
      body: editor.getHTML(),
      participantGroupIds: [...selectedSet],
    })
      .then(response => {
        startSuccessBarSuccess(`${response.data} Mails werden im Hintergrund versendet`);
        setSelectedTextblockId(null);
        setUsedVariant(null);
        setSubject('');
        editor.commands.setContent('');
      })
      .catch(err => {
        closeSuccessBar();
        setError(err.response?.data ?? 'Rundmail konnte nicht gesendet werden.');
        openPopup('roundmailerror', 1);
      });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
  };

  return (
    <React.Fragment>
      <main className={'wideMain'}>
        <OfflineBanner />
        <div className={'innerContainer'}>
            <div className={'generalInformationContainer'}>
              <label className={'innerContainerLabel'}>{(event && eventLabelOf(event)) || 'Spieltag'}</label>
              <Textfield icon={'event'} type={'text'} placeholder={'Datum'} readOnly value={formatDate(event?.date)} />

              <label className={'innerContainerLabel'} id={'roundmailGroupsLabel'}>Empfänger-Gruppen</label>
              <div className={'roundmailGroups'}>
                <div className={'participantGroupChips'} role={'group'} aria-labelledby={'roundmailGroupsLabel'}>
                  {chipGroups.map(g => (
                    <ParticipantGroupChip
                      key={g.id}
                      group={g}
                      label={`${g.pluralName} (${countByGroup.get(g.id) ?? 0})`}
                      active={selectedSet.has(g.id)}
                      onClick={() => toggleGroup(g.id)}
                      title={`Bestätigte ${g.pluralName} mit E-Mail-Adresse`}
                    />
                  ))}
                </div>
                <p className={'roundmailHint'}>
                  {groupsFailed && !groupsLoaded
                    ? 'Die Gruppen konnten nicht geladen werden.'
                    : selectedSet.size === 0 && groupsLoaded
                      ? 'Bitte mindestens eine Gruppe auswählen.'
                      : 'Bestätigte Teilnehmer der gewählten Gruppen (Gruppe am Spieltag) mit E-Mail-Adresse.'}
                </p>
              </div>

              <label className={'innerContainerLabel'}>
                Empfänger ({recipients.length})
              </label>
              <RecipientPreview
                recipients={recipients}
                emptyText={selectedSet.size === 0 ? 'Keine Gruppe ausgewählt' : 'Keine Teilnehmer mit E-Mail in den gewählten Gruppen'}
              />

              <label className={'innerContainerLabel'}>Textbaustein</label>
              <CustomDropdown
                value={selectedTextblockId ? String(selectedTextblockId) : ''}
                onChange={handleDropdownChange}
                icon={'message'}
              >
                <option value=''>– Kein Textbaustein –</option>
                {/* Only once the event is loaded: the text depends on its location (variant). */}
                {(event ? textblocks : []).map(tb => (
                  <option key={tb.id} value={tb.id}>{tb.frontendName}</option>
                ))}
              </CustomDropdown>
              <TextblockVariantHint variant={usedVariant} />

              <label className={'innerContainerLabel'}>Betreff</label>
              <Textfield
                icon={'subject'}
                type={'text'}
                placeholder={'Betreff eingeben ...'}
                value={subject}
                onChange={e => setSubject(e.target.value)}
              />

              <div className={'buttonsIconMailContainer'}>
                <ActionButton icon="send" label="Senden" type={2} onClick={sendMail} disabled={selectedSet.size === 0} />
              </div>
            </div>

            <div className={'sideContainer'}>
              <label className={'innerContainerLabel'}>E-Mail bearbeiten</label>
              <div className={'roundmailPlaceholderInfo'}>
                <Icon icon={'info'} className={'roundmailInfoIcon'} />
                <span>
                  Platzhalter wie <code>{'{Vorname}'}</code>, <code>{'{Datum}'}</code> oder <code>{'{Zusaetze}'}</code> (gebuchte Anmelde-Zusätze) werden beim Versand pro Empfänger automatisch befüllt.
                </span>
              </div>
              <BubbleMenu editor={editor}>
                <div className={'bubbleMenu'}>
                  <Tooltip text='Rückgängig'>
                    <button type='button' onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
                      <Icon className={'bubbleIcon'} icon={'undo'} />
                    </button>
                  </Tooltip>
                  <Tooltip text='Wiederholen'>
                    <button type='button' onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
                      <Icon className={'bubbleIcon'} icon={'redo'} />
                    </button>
                  </Tooltip>
                  <Tooltip text='Fett'>
                    <button type='button' onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'isActive' : ''}>
                      <Icon className={'bubbleIcon'} icon={'format_bold'} />
                    </button>
                  </Tooltip>
                  <Tooltip text='Kursiv'>
                    <button type='button' onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'isActive' : ''}>
                      <Icon className={'bubbleIcon'} icon={'format_italic'} />
                    </button>
                  </Tooltip>
                  <Tooltip text='Link'>
                    <button type='button' onClick={setLink} className={editor.isActive('link') ? 'isActive' : ''}>
                      <Icon className={'bubbleIcon'} icon={'link'} />
                    </button>
                  </Tooltip>
                  <Tooltip text='Liste'>
                    <button type='button' onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'isActive' : ''}>
                      <Icon className={'bubbleIcon'} icon={'format_list_bulleted'} />
                    </button>
                  </Tooltip>
                  <Tooltip text='Farbe'>
                    <input
                      type='color'
                      onInput={e => editor.chain().focus().setColor(e.target.value).run()}
                      value={editor.getAttributes('textStyle').color || '#000000'}
                    />
                  </Tooltip>
                  <Tooltip text='Farbe zurücksetzen'>
                    <button type='button' onClick={() => editor.chain().focus().unsetColor().run()}>
                      <Icon className={'bubbleIcon'} icon={'format_color_reset'} />
                    </button>
                  </Tooltip>
                </div>
              </BubbleMenu>
              <EditorContent editor={editor} />
            </div>
          </div>
      </main>

      <Popup name='roundmailerror' confirmText='Ok' headerText='Fehler' type='error'>
        <p>{error}</p>
      </Popup>
    </React.Fragment>
  );
}

export default RoundMail;
